import React, { useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHeart,
  faLocationDot,
  faStore,
  faStar,
  faPlus,
  faMinus,
  faCartShopping,
  faEye,
  faTag,
  faCrown,
  faFire,
  faBoxOpen,
} from "@fortawesome/free-solid-svg-icons";

const ProductCard = ({
  product,
  quantity = 0,
  isSaved = false,
  onAdd,
  onIncrease,
  onDecrease,
  onToggleSave,
  onView,
}) => {
  const [imageError, setImageError] = useState(false);

  const savings = useMemo(() => {
    return Math.max((product?.mrp || 0) - (product?.sellingPrice || 0), 0);
  }, [product]);

  const savingsPercent = useMemo(() => {
    if (!product?.mrp) return 0;

    return Math.round(
      ((product.mrp - product.sellingPrice) / product.mrp) * 100,
    );
  }, [product]);

  const inStock = (product?.stock || 0) > 0;

  const image =
    !imageError && product?.images && product.images.length > 0
      ? product.images[0]
      : "https://placehold.co/600x600/18181b/f9a8d4?text=HerKart";

  return (
    <div className="group overflow-hidden rounded-3xl border border-fuchsia-500/15 bg-gradient-to-br from-neutral-900 via-neutral-900 to-black shadow-xl transition-all duration-300 hover:-translate-y-1 hover:border-pink-500/40">
      <div className="relative">
        <img
          src={image}
          alt={product?.name}
          loading="lazy"
          onError={() => setImageError(true)}
          className="h-52 w-full object-cover transition duration-500 group-hover:scale-105"
        />

        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />

        <div className="absolute left-3 top-3 flex flex-wrap gap-2">
          {product?.featured && (
            <span className="flex items-center gap-1 rounded-full bg-pink-600 px-3 py-1 text-xs font-semibold text-white shadow-lg">
              <FontAwesomeIcon icon={faCrown} />
              Featured
            </span>
          )}

          {product?.bestseller && (
            <span className="flex items-center gap-1 rounded-full bg-fuchsia-700 px-3 py-1 text-xs font-semibold text-white shadow-lg">
              <FontAwesomeIcon icon={faFire} />
              Bestseller
            </span>
          )}
        </div>

        <button
          type="button"
          onClick={() => onToggleSave?.(product)}
          className={`absolute right-3 top-3 flex h-11 w-11 items-center justify-center rounded-full backdrop-blur transition
            ${
              isSaved
                ? "bg-pink-600 text-white"
                : "bg-black/50 text-white hover:bg-pink-600"
            }`}
        >
          <FontAwesomeIcon icon={faHeart} />
        </button>

        {!inStock && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/75">
            <span className="rounded-full bg-red-600 px-5 py-2 text-sm font-bold tracking-wide text-white">
              OUT OF STOCK
            </span>
          </div>
        )}
      </div>

      <div className="space-y-4 p-5">
        <div className="flex items-center justify-between">
          <span className="rounded-full bg-fuchsia-900/40 px-3 py-1 text-xs font-semibold text-fuchsia-300">
            <FontAwesomeIcon icon={faTag} className="mr-2" />
            {product?.category}
          </span>
        </div>
        <div className="flex items-center gap-1 text-xs lg:text-sm text-amber-400">
          <FontAwesomeIcon icon={faStar} />
          <span className="font-semibold">{product?.rating}</span>
          <span className="text-xs text-gray-400">
            ({product?.totalRatings})
          </span>
        </div>

        <div>
          <h3 className="line-clamp-2 text-sm lg:text-lg font-bold leading-6 text-cyan-200">
            {product?.name}
          </h3>

          <p className="mt-2 text-xs lg:text-sm text-pink-300">
            {product?.entrepreneur}
          </p>
        </div>

        <div className="space-y-2 text-xs lg:text-sm text-gray-400">
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faStore} className="text-pink-400" />
            <span className="truncate">{product?.store}</span>
          </div>

          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faLocationDot} className="text-pink-400" />
            <span>
              {product?.locality}, {product?.city}
            </span>
          </div>
        </div>
        <div className="rounded-2xl border border-fuchsia-500/10 bg-white/[0.03] p-4">
          <div className="flex items-end justify-between">
            <div>
              <div className="flex items-center gap-2">
                <span className="text-sm lg:text-lg font-extrabold text-white">
                  ₹{product?.sellingPrice?.toLocaleString("en-IN")}
                </span>

                {product?.mrp > product?.sellingPrice && (
                  <span className="text-xs lg:text-sm text-gray-500 line-through">
                    ₹{product?.mrp?.toLocaleString("en-IN")}
                  </span>
                )}
              </div>

              {savings > 0 && (
                <div className="mt-1 flex items-center gap-2">
                  <span className="rounded-full bg-emerald-500/15 px-2 py-1 text-xs font-semibold text-emerald-400">
                    Save ₹{savings.toLocaleString("en-IN")}
                  </span>

                  <span className="text-xs font-medium text-emerald-300">
                    {savingsPercent}% OFF
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-row text-left mt-2">
            <div className="text-xs uppercase tracking-wide text-gray-500">
              Unit
            </div>

            <div className="font-semibold text-gray-200">{product?.unit}</div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {product?.pickup && (
            <span className="rounded-full border border-pink-500/20 bg-pink-500/10 px-3 py-1 text-xs font-medium text-pink-300">
              Pickup
            </span>
          )}

          {product?.localDelivery && (
            <span className="rounded-full border border-purple-500/20 bg-purple-500/10 px-3 py-1 text-xs font-medium text-purple-300">
              Local Delivery
            </span>
          )}

          {product?.cod && (
            <span className="rounded-full border border-fuchsia-500/20 bg-fuchsia-500/10 px-3 py-1 text-xs font-medium text-fuchsia-300">
              COD
            </span>
          )}
        </div>

        <div className="flex flex-cols items-center justify-between rounded-2xl border border-fuchsia-500/10 bg-black/20 px-4 py-3">
          <div className="flex flex-cols items-center gap-2">
            <FontAwesomeIcon
              icon={faBoxOpen}
              className={`${inStock ? "text-emerald-400" : "text-red-400"}`}
            />

            <div className="flex-cols">
              <div className="text-xs uppercase tracking-wide text-gray-500">
                Availability
              </div>

              <div
                className={`text-xs lg:text-sm font-semibold ${
                  inStock ? "text-emerald-400" : "text-red-400"
                }`}
              >
                {inStock ? `${product?.stock} in stock` : "Out of Stock"}
              </div>
            </div>
          </div>
        </div>
        <div>
          <button
            type="button"
            onClick={() => onView?.(product)}
            className="flex items-center gap-1 rounded-xl border border-fuchsia-500/20 w-fit px-2 py-1 text-xs lg:text-sm font-medium text-fuchsia-300 transition hover:bg-fuchsia-500/10"
          >
            <FontAwesomeIcon icon={faEye} />
            View
          </button>
        </div>
        <div className="pt-1">
          {inStock ? (
            quantity > 0 ? (
              <div className="flex items-center justify-between rounded-2xl bg-gradient-to-r from-pink-600 to-fuchsia-600 p-1 shadow-lg">
                <button
                  type="button"
                  onClick={() => onDecrease?.(product)}
                  className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/15 text-white transition hover:bg-white/25"
                  aria-label="Decrease quantity"
                >
                  <FontAwesomeIcon icon={faMinus} />
                </button>

                <div className="flex flex-col items-center justify-center px-4">
                  <span className="text-lg lg:text-xl font-bold text-white">
                    {quantity}
                  </span>
                  <span className="text-[10px] uppercase tracking-wider text-pink-100">
                    In Cart
                  </span>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    if (quantity < product.stock) {
                      onIncrease?.(product);
                    }
                  }}
                  disabled={quantity >= product.stock}
                  className={`flex h-11 w-11 items-center justify-center rounded-xl text-white transition ${
                    quantity >= product.stock
                      ? "cursor-not-allowed bg-white/10 opacity-50"
                      : "bg-white/15 hover:bg-white/25"
                  }`}
                  aria-label="Increase quantity"
                >
                  <FontAwesomeIcon icon={faPlus} />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => onAdd?.(product)}
                className="text-xs lg:text-sm flex w-fit px-2 py-1 items-center justify-center gap-3 rounded-2xl bg-gradient-to-r from-pink-600 via-fuchsia-600 to-purple-600 font-semibold text-white shadow-lg transition-all duration-300 hover:scale-[1.02] hover:shadow-pink-500/30"
              >
                <FontAwesomeIcon icon={faCartShopping} />
                Add to Cart
              </button>
            )
          ) : (
            <button
              type="button"
              disabled
              className="text-xs lg:text-sm flex w-fit px-2 py-1 cursor-not-allowed items-center justify-center gap-3 rounded-2xl bg-neutral-800 font-semibold text-gray-500"
            >
              <FontAwesomeIcon icon={faBoxOpen} />
              Out of Stock
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
